import { supabase } from '../lib/supabase';

/**
 * Tenant Management Service
 * Handles tenant (sub-account) operations
 */

export const tenantService = {
  /**
   * Get all tenants (super admin only)
   */
  async getAllTenants() {
    try {
      const { data, error } = await supabase?.from('tenants')?.select(`
          *,
          subscription:tenant_subscriptions(
            *,
            tier:subscription_tiers(*)
          )
        `)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get tenants error:', error);
      throw error;
    }
  },

  /**
   * Get tenant by ID
   */
  async getTenantById(tenantId) {
    try {
      const { data, error } = await supabase?.from('tenants')?.select(`
          *,
          subscription:tenant_subscriptions(
            *,
            tier:subscription_tiers(*)
          )
        `)?.eq('id', tenantId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get tenant error:', error);
      throw error;
    }
  },

  /**
   * Get tenant by subdomain
   */
  async getTenantBySubdomain(subdomain) {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.select('*')
        ?.eq('subdomain', subdomain)
        ?.single();
      
      if (error) {
        if (error?.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching tenant by subdomain:', error);
      throw error;
    }
  },

  /**
   * Check subdomain availability
   */
  async checkSubdomainAvailability(subdomain) {
    try {
      // Validate subdomain format
      if (!subdomain || !/^[a-z0-9-]+$/?.test(subdomain)) {
        return {
          available: false,
          message: 'Invalid subdomain format',
          suggestions: []
        };
      }

      // Check if subdomain exists
      const { data, error } = await supabase
        ?.from('tenants')
        ?.select('subdomain')
        ?.eq('subdomain', subdomain)
        ?.maybeSingle();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      const isAvailable = !data;

      // Generate suggestions if taken
      const suggestions = [];
      if (!isAvailable) {
        const baseName = subdomain?.replace(/-\d+$/, '');
        for (let i = 1; i <= 3; i++) {
          const suggestion = `${baseName}-${i}`;
          const { data: existingData } = await supabase
            ?.from('tenants')
            ?.select('subdomain')
            ?.eq('subdomain', suggestion)
            ?.maybeSingle();
          
          if (!existingData) {
            suggestions?.push(suggestion);
          }
        }
      }

      return {
        available: isAvailable,
        message: isAvailable ? 'Subdomain is available' : 'Subdomain is already taken',
        suggestions
      };
    } catch (error) {
      console.error('Check subdomain availability error:', error);
      throw error;
    }
  },

  /**
   * Create new tenant
   */
  async createTenant(tenantData) {
    try {
      const { data, error } = await supabase?.from('tenants')?.insert({
          name: tenantData?.name,
          subdomain: tenantData?.subdomain,
          custom_domain: tenantData?.customDomain,
          settings: tenantData?.settings || {},
          logo_url: tenantData?.logoUrl,
          primary_color: tenantData?.primaryColor || '#3B82F6',
          secondary_color: tenantData?.secondaryColor || '#8B5CF6',
          industry_id: tenantData?.industryId
        })?.select()?.single();

      if (error) throw error;

      // Create default subscription if tier provided
      if (tenantData?.tierId) {
        await this.createSubscription(data?.id, tenantData?.tierId);
      }

      return data;
    } catch (error) {
      console.error('Create tenant error:', error);
      throw error;
    }
  },

  /**
   * Update tenant
   */
  async updateTenant(tenantId, updates) {
    try {
      const { data, error } = await supabase?.from('tenants')?.update(updates)?.eq('id', tenantId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update tenant error:', error);
      throw error;
    }
  },

  /**
   * Delete tenant
   */
  async deleteTenant(tenantId) {
    try {
      const { error } = await supabase?.from('tenants')?.delete()?.eq('id', tenantId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete tenant error:', error);
      throw error;
    }
  },

  /**
   * Create subscription for tenant
   */
  async createSubscription(tenantId, tierId, billingCycle = 'monthly') {
    try {
      const periodEnd = new Date();
      periodEnd?.setMonth(periodEnd?.getMonth() + (billingCycle === 'annual' ? 12 : 1));

      const { data, error } = await supabase?.from('tenant_subscriptions')?.insert({
          tenant_id: tenantId,
          tier_id: tierId,
          status: 'trial',
          billing_cycle: billingCycle,
          current_period_start: new Date()?.toISOString(),
          current_period_end: periodEnd?.toISOString(),
          next_billing_date: periodEnd?.toISOString(),
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create subscription error:', error);
      throw error;
    }
  },

  /**
   * Get subscription tiers
   */
  async getSubscriptionTiers() {
    try {
      const { data, error } = await supabase?.from('subscription_tiers')?.select('*')?.eq('is_active', true)?.order('base_price', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get subscription tiers error:', error);
      throw error;
    }
  },

  /**
   * Get tenant users
   */
  async getTenantUsers(tenantId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('tenant_id', tenantId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get tenant users error:', error);
      throw error;
    }
  },
};